<template>
    <v-container fluid>
        <v-row class="email-page-container px-6">
            <v-col
                class="mt-4 pb-2 px-0 text-h6"
                cols="9"
            >
                Email
            </v-col>
            <v-col
                class="mt-4 pb-2 px-0 text-right"
                cols="3"
            >
                <v-btn
                    color="primary"
                    outlined
                    class="text-capitalize text-right"
                    :disabled="isLoadingData"
                    @click="refresh"
                >
                    <v-icon left v-if="!isLoadingData">
                        mdi-refresh
                    </v-icon>
                    <v-progress-circular
                        v-else
                        left
                        indeterminate
                        class="ml-0"
                        color="gray"
                        size="16"
                        width="2"
                    />
                    {{ isLoadingData ? 'Refreshing...' : 'Refresh' }}
                </v-btn>
            </v-col>
            <v-col
                class="px-0"
                cols="12"
            >
                <v-tabs
                    v-model="tab"
                    background-color="var(--ll-surface-subtle)"
                >
                    <v-tab
                        key="1"
                        href="#queue-tab"
                    >
                        Queue 
                        <v-chip
                            class="ml-2 text-caption email-chip"
                            outlined
                        >
                            {{ queueShowData.length }}
                        </v-chip>
                    </v-tab>
                    <v-tab
                        key="2"
                        href="#user-email-history"
                    >
                        History 
                        <v-chip
                            class="ml-2 text-caption email-chip"
                            outlined
                        >
                            {{ historyShowData.length }}
                        </v-chip>
                    </v-tab>
                </v-tabs>
                <v-tabs-items
                    v-model="tab" 
                    :touchless="true"
                    class="tabs-stop-transition"
                >
                    <v-tab-item
                        key="1"
                        value="queue-tab"
                    >
                        <v-menu
                            ref="queueMenu"
                            :close-on-content-click="false"
                            offset-y
                            :nudge-width="200"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    color="primary"
                                    outlined
                                    v-bind="attrs"
                                    class="text-capitalize my-3"
                                    v-on="on"
                                >
                                    <v-icon left>
                                        mdi-filter-variant
                                    </v-icon>
                                    Filter
                                </v-btn>
                                <v-row
                                    v-show="hasQueueFilterApplied"
                                    class="filter-chips"
                                >
                                    <v-chip
                                        small
                                        class="ma-2"
                                        close
                                        @click:close="clearQueueFilter()"
                                    >
                                        {{ queueDateText }}
                                    </v-chip>
                                    <v-chip
                                        color="white"
                                        class="ma-1"
                                        text-color="#1976D2"
                                        @click="clearQueueFilter"
                                    >
                                        Clear
                                    </v-chip>
                                </v-row>
                            </template>
                            <v-card class="filter-email-card">
                                <v-list>
                                    <v-row class="align-center justify-space-between">
                                        <v-col
                                            cols="8"
                                            class="filter-email-title px-6"
                                        >
                                            Filter
                                        </v-col>
                                        <v-col
                                            class="clear-email-filter"
                                            cols="3"
                                        >
                                            <a 
                                                :disabled="queueDateFilter.length == 0"
                                                :class="queueDateFilter.length > 0 ? 'enabled-btn' : 'disabled-btn'"
                                                @click="clearQueueFilter"
                                            >Clear</a>
                                        </v-col>
                                    </v-row>
                                    <v-row>
                                        <v-col
                                            class="py-0 pr-8 pl-6"
                                            cols="12"
                                        >
                                            <v-menu
                                                ref="queueDateMenu"
                                                v-model="queueDateMenu"
                                                :close-on-content-click="false"
                                                :return-value.sync="queueDateFilter"
                                                transition="scale-transition"
                                                offset-y
                                                min-width="auto"
                                            >
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-text-field
                                                        v-model="queueDateText"
                                                        label="Date Range"
                                                        prepend-inner-icon="mdi-calendar"
                                                        readonly
                                                        v-bind="attrs"
                                                        outlined
                                                        dense
                                                        v-on="on"
                                                    />
                                                </template>
                                                <v-date-picker
                                                    v-model="queueDateFilter"
                                                    no-title
                                                    range
                                                    scrollable
                                                >
                                                    <v-spacer />
                                                    <v-btn
                                                        text
                                                        color="primary"
                                                        @click="queueDateMenu = false"
                                                    >
                                                        Cancel
                                                    </v-btn>
                                                    <v-btn
                                                        text
                                                        color="primary"
                                                        @click="$refs.queueDateMenu.save(queueDateFilter)"
                                                    >
                                                        OK
                                                    </v-btn>
                                                </v-date-picker>
                                            </v-menu>
                                        </v-col>
                                    </v-row>
                                    <v-row class="mx-0 my-0">
                                        <v-col class="py-0">
                                            <v-btn
                                                class="ma-1 action-btn text-subtitle-2"
                                                style="float: right;"
                                                depressed
                                                color="primary"
                                                :disabled="queueDateFilter.length == 0"
                                                @click="applyQueueFilter"
                                            >
                                                Apply
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-list>
                            </v-card>
                        </v-menu>
                        <v-data-table
                            id="queue-table"
                            :loading="isLoadingData"
                            :headers="queueHeaders"
                            :items="queueShowData"
                            :items-per-page="queueItemsPerPage"
                            fixed-header
                            :height="`calc(100vh - 400px)`"
                            class="table-elevation"
                        >
                            <template v-slot:[`item.queued_date`]="{item}">
                                {{ `${moment(item.queued_date).format('MM/DD/YYYY, hh:mm a')} (${moment(item.queued_date).fromNow()})` }}
                            </template>
                            <template v-slot:[`item.delivered`]="{item}">
                                {{ item.failed_attempts ? 'Yes' : 'No' }}
                            </template>
                        </v-data-table>
                        <v-divider />
                    </v-tab-item>
                    <v-tab-item
                        key="2"
                        value="user-email-history"
                    >
                        <v-menu
                            ref="historyMenu"
                            :close-on-content-click="false"
                            offset-y
                            :nudge-width="200"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    color="primary"
                                    outlined
                                    v-bind="attrs"
                                    class="text-capitalize my-3"
                                    v-on="on"
                                >
                                    <v-icon left>
                                        mdi-filter-variant
                                    </v-icon>
                                    Filter
                                </v-btn>
                                <v-row
                                    v-show="hasHistoryFilterApplied"
                                    class="filter-chips"
                                >
                                    <v-chip
                                        small
                                        class="ma-2"
                                        close
                                        @click:close="clearHistoryFilter()"
                                    >
                                        {{ historyDateText }}
                                    </v-chip>
                                    <v-chip
                                        color="white"
                                        class="ma-1"
                                        text-color="#1976D2"
                                        @click="clearHistoryFilter"
                                    >
                                        Clear
                                    </v-chip>
                                </v-row>
                            </template>
                            <v-card class="filter-email-card">
                                <v-list>
                                    <v-row class="align-center justify-space-between">
                                        <v-col
                                            cols="8"
                                            class="filter-email-title px-6"
                                        >
                                            Filter
                                        </v-col>
                                        <v-col
                                            class="clear-email-filter"
                                            cols="3"
                                        >
                                            <a 
                                                :disabled="historyDateFilter.length == 0"
                                                :class="historyDateFilter.length > 0 ? 'enabled-btn' : 'disabled-btn'"
                                                @click="clearHistoryFilter"
                                            >Clear</a>
                                        </v-col>
                                    </v-row>
                                    <v-row>
                                        <v-col
                                            class="py-0 pr-8 pl-6"
                                            cols="12"
                                        >
                                            <v-menu
                                                ref="historyDateMenu"
                                                v-model="historyDateMenu"
                                                :close-on-content-click="false"
                                                :return-value.sync="historyDateFilter"
                                                transition="scale-transition"
                                                offset-y
                                                min-width="auto"
                                            >
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-text-field
                                                        v-model="historyDateText"
                                                        label="Date Range"
                                                        prepend-inner-icon="mdi-calendar"
                                                        readonly
                                                        v-bind="attrs"
                                                        outlined
                                                        dense
                                                        v-on="on"
                                                    />
                                                </template>
                                                <v-date-picker
                                                    v-model="historyDateFilter"
                                                    no-title
                                                    range
                                                    scrollable
                                                >
                                                    <v-spacer />
                                                    <v-btn
                                                        text
                                                        color="primary"
                                                        @click="historyDateMenu = false"
                                                    >
                                                        Cancel
                                                    </v-btn>
                                                    <v-btn
                                                        text
                                                        color="primary"
                                                        @click="$refs.historyDateMenu.save(historyDateFilter)"
                                                    >
                                                        OK
                                                    </v-btn>
                                                </v-date-picker>
                                            </v-menu>
                                        </v-col>
                                    </v-row>
                                    <v-row class="mx-0 my-0">
                                        <v-col class="py-0">
                                            <v-btn
                                                class="ma-1 action-btn text-subtitle-2"
                                                style="float: right;"
                                                depressed
                                                color="primary"
                                                :disabled="historyDateFilter.length == 0"
                                                @click="applyHistoryFilter"
                                            >
                                                Apply
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-list>
                            </v-card>
                        </v-menu>
                        <v-data-table
                            id="history-table"
                            :loading="isLoadingData"
                            :headers="historyHeaders"
                            :items="historyShowData"
                            :items-per-page="historyItemsPerPage"
                            fixed-header
                            :height="`calc(100vh - 400px)`"
                            class="table-elevation"
                        >
                            <template v-slot:[`item.info`]="{ item }">
                                <v-btn text color="primary" @click="showInfoDialog(item)">
                                    <v-icon>mdi-information</v-icon>
                                </v-btn>
                            </template>
                            <template v-slot:[`item.delivery_disposition`]="{item}">
                                <v-icon
                                    small
                                    :color="statusColors[item.delivery_disposition] || statusColors.default"
                                    v-if="['delivered','dropped'].includes(item.delivery_disposition)">
                                    {{ item.delivery_disposition == 'delivered' ? 'mdi-check' : 'mdi-close' }}
                                </v-icon>
                                {{ item.delivery_disposition || 'N/A' }}
                            </template>
                            <template v-slot:[`item.destination`]="{item}">
                                {{ item.email || item.mail_to }}
                            </template>
                            <template v-slot:[`item.delivered_date`]="{item}">
                                {{ item.delivered_date ? `${moment(item.delivered_date).format('ddd MM/DD/YYYY, hh:mm a')} (${moment(item.delivered_date).fromNow()})` : item.last_error }}
                            </template>
                        </v-data-table>
                        <v-dialog v-model="infoDialog.show" max-width="960px">
                            <v-card>
                                <v-card-title>{{ (infoDialog.item && infoDialog.item.subject) }}</v-card-title>
                                <v-card-text>
                                    <p><b>To:</b> {{ (infoDialog.item && infoDialog.item.email) || (infoDialog.item && infoDialog.item.mail_to) }}</p>
                                    <p>
                                        <v-simple-table
                                            fixed-header
                                            height="300px"
                                        >
                                            <template v-slot:default>
                                                <thead>
                                                    <tr>
                                                        <th scope="col">Date</th>
                                                        <th scope="col">Status</th>
                                                        <th scope="col">Reason</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr
                                                        v-for="hook in selectedItemWebhooks"
                                                        :key="hook.hook.id"
                                                    >
                                                        <td>
                                                            {{ moment(hook.hook.created_at).format('MM/DD/YYYY, hh:mm a') }}
                                                        </td>
                                                        <td>
                                                            <v-icon
                                                                small
                                                                :color="statusColors[hook.status] || statusColors.default"
                                                                v-if="['delivered','dropped'].includes(hook.status)"
                                                            >
                                                                {{ hook.status == 'delivered' ? 'mdi-check' : 'mdi-close' }}
                                                            </v-icon>
                                                            {{ hook.status }}
                                                        </td>
                                                        <td>
                                                            {{ (hook.hook.responseObj && hook.hook.responseObj.reason) || (hook.hook.responseObj && hook.hook.responseObj.response) }}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </template>
                                        </v-simple-table>
                                    </p>
                                </v-card-text>
                                <v-card-actions>
                                    <v-spacer></v-spacer>
                                    <v-btn color="primary" text @click="infoDialog.show = false">Close</v-btn>
                                </v-card-actions>
                            </v-card>
                        </v-dialog>                        
                        <v-divider />
                    </v-tab-item>
                </v-tabs-items>
            </v-col>
        </v-row>
    </v-container>
</template>
<script>
import webtmsBackend from '@/services/webtmsBackend';
import moment from 'moment';
export default {
    name: 'EmailQueue',
    data: () => ({
        tab: null,
        isLoadingData: false,
        queueDateMenu: false,
        queueDateFilter: [],
        moment: moment,
        queueHeaders: [
            {text: 'Destination', value: 'mail_to', align: 'left', class: 'header-color'},
            {text: 'Subject', value: 'subject', align: 'left'},
            {text: 'Queued', value: 'queued_date', align: 'left'},
            {text: 'Disposition', value: 'delivery_disposition', align: 'left'}
        ],
        queueDataMenu: false,
        queueData: [],
        queueShowData: [],
        queueItemsPerPage: 15,
        queueFilterMenu: false,
        queueDataLoading: false,
        hasQueueFilterApplied: false,

        historyDateMenu: false,
        historyDateFilter: [],
        historyHeaders: [
            {text: 'Info', value: 'info', align: 'left'},
            {text: 'Disposition', value: 'delivery_disposition', align: 'left'},
            {text: 'Destination', value: 'destination', align: 'left'},
            {text: 'Subject', value: 'subject', align: 'left'},
            {text: 'Status Date', value: 'delivered_date', align: 'left'}
        ],
        historyDataMenu: false,
        historyData: [],
        historyShowData: [],
        historyItemsPerPage: 15,
        hasHistoryFilterApplied: false,
        // infoDialogShow: false,
        // info_selectedItem: null,
        infoDialog: {
            show: false,
            item: null
        },
        statusColors: {
            deferred: 'yellow',
            delivered: 'green',
            dropped: 'red',
            queued: 'green',
            relayed: 'yellow',
            default: 'transparent'
        }        
    }),
    computed: {
        queueDateText() {
            return this.queueDateFilter.length > 0 ? this.queueDateFilter.join(' ~ ') : '';
        },
        historyDateText() {
            return this.historyDateFilter.length > 0 ? this.historyDateFilter.join(' ~ ') : '';
        },
        selectedItemWebhooks() {
            if(!this.infoDialog.item || !this.infoDialog.item.delivery_webhooks) {
                return [];
            }
            let webhooks = JSON.parse(this.infoDialog.item.delivery_webhooks);
            // remove/ignore "click" and "open" events
            webhooks = webhooks.filter(webhook => !['click','open'].includes(webhook.status));
            // sort the webhooks by id
            webhooks.sort((a, b) => a.hook.id - b.hook.id);
            // parse JSON hook.response
            webhooks.forEach(webhook => {
                try {
                    webhook.hook.responseObj = JSON.parse(webhook.hook.response);
                } catch (e) {
                    webhook.hook.responseObj = { reason: '<no response information>'};
                }
            });
            return webhooks;
        }
    },
    created() {
        this.getData();
    },
    methods: {
        async getData() {
            try {
                this.isLoadingData = true;
                let emailList = await webtmsBackend.getEmailList();
                this.queueData = [...emailList.queue];

                let emailHistoryList = await webtmsBackend.getEmailHistory();
                this.historyData = [...emailHistoryList.emailHistory];

                this.applyQueueFilter();
                this.applyHistoryFilter();
            } catch (e) {
                console.error(e);
            } finally {
                this.isLoadingData = false;
            }
        },
        clearQueueFilter() {
            this.queuePage = 1;
            this.queueDateFilter = [];
            this.queueShowData = [...this.queueData];
            this.hasQueueFilterApplied = false;
            this.$refs.queueMenu.save();
        },
        applyQueueFilter() {
            if(this.queueDateFilter.length > 0) {
                let startDate;
                let endDate;
                if(this.queueDateFilter.length > 1) {
                    startDate = moment(this.queueDateFilter[0]).startOf('d').unix();
                    endDate = moment(this.queueDateFilter[1]).endOf('d').unix();
                } else {
                    startDate = moment(this.queueDateFilter[0]).startOf('d').unix();
                    endDate = moment(this.queueDateFilter[0]).endOf('d').unix();
                }
                this.queueShowData = [...this.queueData].filter(item => (moment(item.queued_date).unix() >= startDate && moment(item.queued_date).unix() <= endDate) );
                this.hasQueueFilterApplied = true;
                this.$refs.queueMenu.save();
            } else {
                this.queueShowData = [...this.queueData];
            }
        },
        clearHistoryFilter() {
            this.historyPage = 1;
            this.historyDateFilter = [];
            this.historyShowData = [...this.historyData];
            this.hasHistoryFilterApplied = false;
            this.$refs.historyMenu.save();
        },
        applyHistoryFilter() {
            if(this.historyDateFilter.length > 0) {
                let startDate;
                let endDate;
                if(this.historyDateFilter.length > 1) {
                    startDate = moment(this.historyDateFilter[0]).startOf('d').unix();
                    endDate = moment(this.historyDateFilter[1]).endOf('d').unix();
                } else {
                    startDate = moment(this.historyDateFilter[0]).startOf('d').unix();
                    endDate = moment(this.historyDateFilter[0]).endOf('d').unix();
                }
                this.historyShowData = [...this.historyData].filter(item => (moment(item.delivered_date).unix() >= startDate && moment(item.delivered_date).unix() <= endDate) );
                this.hasHistoryFilterApplied = true;
                this.$refs.historyMenu.save();
            } else {
                this.historyShowData = [...this.historyData];
            }
        },
        refresh() {
            this.getData();
        },
        showInfoDialog(item) {
            this.infoDialog.item = item;
            this.infoDialog.show = true;
            console.info(item);
        }
    }
};
</script>
<style>
    .table-elevation {
        border: 1px solid rgb(0 0 0 / 20%);
        border-bottom: none;
        box-shadow: 0px 1px 1px -1px rgb(0 0 0 / 20%), 0px 0px 0px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%) !important;
    }
    .filter-email-card {
        height: 100%;
        overflow: hidden;
    }
    .filter-email-card .filter-email-title {
        font-style: normal;
        font-weight: 500;
        font-size: 16px;
        line-height: 28px;
    }
    .filter-email-card .clear-email-filter a {
        font-weight: 500;
        font-size: 14px;
        line-height: 20px;
        letter-spacing: 0.0025em;
    }
    .enabled-btn {
        color: #1976D2;
    }
    .disabled-btn {
        color: var(--ll-border);
    }
    .email-chip {
        border-radius: 20px !important;
        height: 18px !important;
        padding: 0px 4px !important;
    }
    .tabs-stop-transition .v-window-x-transition-enter-active, .v-window-x-transition-leave-active, .v-window-x-reverse-transition-enter-active, .v-window-x-reverse-transition-leave-active, .v-window-y-transition-enter-active, .v-window-y-transition-leave-active, .v-window-y-reverse-transition-enter-active, .v-window-y-reverse-transition-leave-active {
        transition: none !important;
    }
    #queue-table th {
        background: var(--ll-surface-subtle) !important;
    }
    #history-table th {
        background: var(--ll-surface-subtle) !important;
    }

    .tabs-stop-transition .filter-chips {
        margin-left: 5px;
        display: inline-block;
    }
</style>
